import type { ResolvedCompanyBranding } from './companyBranding';
import { getPlatformDefaultBranding } from './companyBranding';

export type AiChatSupportContact = {
  companyName: string;
  phone: string;
  phoneTelHref: string;
  email: string;
  phoneLine: string;
  contactLine: string;
};

export function buildAiChatSupportContact(branding: ResolvedCompanyBranding): AiChatSupportContact {
  const defaults = getPlatformDefaultBranding();
  const companyName = branding.companyName?.trim() || defaults.companyName;
  const phone = branding.phone?.trim() || defaults.phone || '';
  const email = branding.email?.trim() || defaults.email || '';
  const phoneDigits = phone.replace(/\D/g, '');
  const phoneTelHref = phoneDigits ? `tel:+${phoneDigits.replace(/^\+/, '')}` : '';
  const phoneLine = phone ? `Or call us at ${phone}` : '';
  const contactLine = email && phone ? `${email} or ${phone}` : email || phone || 'our support team';

  return {
    companyName,
    phone,
    phoneTelHref,
    email,
    phoneLine,
    contactLine,
  };
}

export function buildStandaloneEnquiryWelcome(firstName: string | undefined, companyName: string): string {
  const name = firstName?.trim() || 'there';
  return `Hey ${name}! 👋 Welcome to ${companyName}!\n\nI'm Bubbles, your booking assistant. I see you've already shown interest in our services - awesome!\n\nI'm here to make booking quick and easy for you.\n\nSo, what kind of cleaning service are you thinking about?`;
}

export function buildStandaloneChatInitialWelcome(companyName: string): string {
  return `Namaskaram! 👋 I'm Bubbles from ${companyName}.\n\nI can help you with:\n📅 Book a cleaning (2 min)\n💰 Instant pricing\n🎁 Today's offers\n\nWhat can I help you with?`;
}

export function buildBookingConfirmedThanks(companyName: string): string {
  return `Thank you for choosing ${companyName}! 🎉`;
}

let runtimeSupport: AiChatSupportContact | null = null;

/** Set tenant-aware support contact for AI chat / knowledge base (from TenantBrandProvider). */
export function setRuntimeAiSupportContact(contact: AiChatSupportContact): void {
  runtimeSupport = contact;
}

export function getRuntimeAiSupportContact(): AiChatSupportContact {
  return runtimeSupport ?? buildAiChatSupportContact(getPlatformDefaultBranding());
}

export function buildWhatsAppSupportLink(message?: string, contact?: AiChatSupportContact): string {
  const support = contact ?? getRuntimeAiSupportContact();
  const phoneDigits = support.phone.replace(/\D/g, '');
  if (!phoneDigits) {
    const fallback = getPlatformDefaultBranding().phone.replace(/\D/g, '');
    if (!fallback) return '';
    const waPhone = fallback.replace(/^\+/, '');
    const defaultMessage = encodeURIComponent('Hi, I need help with my booking.');
    const customMessage = message ? encodeURIComponent(message) : defaultMessage;
    return `https://wa.me/${waPhone}?text=${customMessage}`;
  }
  const waPhone = phoneDigits.replace(/^\+/, '');
  const defaultMessage = encodeURIComponent('Hi, I need help with my booking.');
  const customMessage = message ? encodeURIComponent(message) : defaultMessage;
  return `https://wa.me/${waPhone}?text=${customMessage}`;
}

/** Display line for support phone in AI replies, e.g. "Or call +91 9995216660". */
export function buildAiSupportPhoneLine(prefix = 'Or call'): string {
  const phone = getRuntimeAiSupportContact().phone?.trim();
  return phone ? `${prefix} ${phone}` : '';
}
